import * as React from "react";
import PropTypes from "prop-types";
import { withColorsProvider } from "nx-components/ColorsProvider";
import CollapsibleList from "nx-components/Pivot/CollapsibleList";
import PartialCheckbox from "nx-components/Pivot/PartialCheckbox";
import Checkbox from "nx-components/Checkbox";
import Button from "nx-components/Button";
import Dialog from "nx-components/Dialog";
import prefixStyles from "nx-components/utils/prefixStyles";
import renderInDestroyableRoot from "nexia_mobile/components/Dialogs/renderInDestroyableRoot";
import DeviceSorter from "nexia_mobile/models/device_sorter";
import Tappable from "nx-components/Tappable";
import Store from "nexia_mobile/flux_like/store";
import { getLanguageSet } from "nx-components/Locales";
import Dispatch from "nexia_mobile/flux_like/dispatch";

class PivotDevicesGroupsDialog extends React.Component {
  language = getLanguageSet().nexia_mobile.dialogs.PivotDevicesGroupsDialog;
  static propTypes = {
    open: PropTypes.bool,
    onChoose: PropTypes.func,
    onCancel: PropTypes.func,
    closeDialog: PropTypes.func,
    targetId: PropTypes.string,
    isSavingSchedule: PropTypes.bool,
    colors: PropTypes.object, // ColorSet
  };

  state = {
    buildings: [],
  };

  _getStyles() {
    const { colors } = this.props;

    return prefixStyles({
      container: {
        display: "flex",
        flexDirection: "column",
        margin: 12,
        // oddly enough, this fixes the positioning of the scrollbar on
        // Galaxy s6 [bug #141988949]. issue is caused by usage of mui RadioButton.
        transform: "translate3d(0,0,0)",
        maxWidth: "80vw",
      },
      title: {
        color: colors.SECONDARY,
        marginBottom: 12,
        flex: "0 0 auto",
      },
      list: {
        overflowY: "auto",
        overflowX: "hidden",
        flex: "1 1 auto",
        maxHeight: "50vh",
      },
      buttons: {
        flex: "0 0 auto",
        display: "flex",
        justifyContent: "flex-end",
      },
      button: {
        margin: "0.2em 0.1em",
      },
      row: {
        display: "flex",
        alignItems: "center",
      },
      checkbox: {
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        marginBottom: 12,
      },
      label: {
        flex: "1 1 auto",
        cursor: "pointer",
        paddingLeft: 8,
        paddingRight: 8,
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      partialCheckbox: {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: -1,
      },
    });
  }

  UNSAFE_componentWillMount() {
    const { targetId } = this.props;
    const { buildings } = this.state;
    const locations = Store.session.get("locations");
    const targetBuildingId = Store.appState.currentLocation.id;
    let buildingIds = [];

    this.sortedBuildings = [];
    this.buildingNames = [];
    this.deviceCounts = [];

    locations.forEach((location) => {
      buildingIds.push(location.get("id"));
      this.sortedBuildings.push(
        DeviceSorter.sort(location.get("devices").map((device) => device))
      );
      this.buildingNames.push(location.get("name"));
      this.deviceCounts.push(location.get("devices").length);
    });

    const lonelyDisabledCheck = (newBuilding, device) => {
      if (this._isConnected(device)) {
        return;
      }
      newBuilding.totalDisabledStats++;
    };
    const groupDisabledCheck = (
      newBuilding,
      device,
      groupStat,
      thermostatId
    ) => {
      if (this._isConnected(groupStat)) {
        return;
      }
      if (!newBuilding.disabledGroupStats[thermostatId]) {
        newBuilding.disabledGroupStats[thermostatId] = 0;
      }
      newBuilding.disabledGroupStats[thermostatId]++;
      if (
        device.getGroupMembers().length ===
        newBuilding.disabledGroupStats[thermostatId]
      ) {
        newBuilding.totalDisabledStats++;
      }
    };

    // Find the intial targeted thermostat. Move it to the top of the list and select it by default.
    this.sortedBuildings.forEach((sortedBuilding, buildingIndex) => {
      let newBuilding = this._buildingTemplate();
      let isTargetBuilding = buildingIds[buildingIndex] === targetBuildingId;
      sortedBuilding.forEach((device, statIndex) => {
        let thermostatId = device.get("id");
        if (device.get("type") === "group") {
          device.getGroupMembers().forEach((groupStat, groupIndex) => {
            groupDisabledCheck(newBuilding, device, groupStat, thermostatId);
            if (groupStat.get("id") === targetId) {
              sortedBuilding.unshift(sortedBuilding.splice(statIndex, 1)[0]);
              newBuilding.lonelyStats.push(0);
              newBuilding.groupStats[0] = [groupIndex];
            }
          });
        } else {
          lonelyDisabledCheck(newBuilding, device);
          if (isTargetBuilding && device.get("id") === targetId) {
            sortedBuilding.unshift(sortedBuilding.splice(statIndex, 1)[0]);
            newBuilding.lonelyStats.push(0);
          }
        }
      });
      if (isTargetBuilding) {
        buildings.unshift(newBuilding);
        [this.sortedBuildings, this.buildingNames, this.deviceCounts].forEach(
          (subject) => {
            subject.unshift(subject.splice(buildingIndex, 1)[0]);
          }
        );
      } else {
        buildings.push(newBuilding);
      }
    });
    this.setState(buildings);
  }

  _isConnected = (device) => {
    const { isSavingSchedule } = this.props;
    return (
      device.get("is_connected") ||
      (isSavingSchedule && device.get("scheduling_enabled"))
    );
  };

  _buildingTemplate = () => {
    return {
      lonelyStats: [],
      groupStats: [],
      totalDisabledStats: 0,
      disabledGroupStats: [],
    };
  };

  _addRemoveThermostat = (
    buildingIndex,
    modelIndex,
    childIndex = -1,
    type = null
  ) => {
    const { buildings } = this.state;
    const model = this._getSortedModel(buildingIndex, modelIndex, childIndex);
    if (!this._isConnected(model)) {
      return;
    }
    let building = this._getOrCreateBuilding(buildingIndex);
    let isGroup = model.get("type") === "group";

    if (isGroup || childIndex > -1) {
      // Group logic
      if (isGroup) {
        const members = model.getGroupMembers().models;
        if (
          !building.groupStats[modelIndex] ||
          !this._isGroupChecked(buildingIndex, modelIndex, building)
        ) {
          this._toggleAllGroupStats(buildingIndex, modelIndex, members, "add");
        } else {
          this._toggleAllGroupStats(
            buildingIndex,
            modelIndex,
            members,
            "remove"
          );
        }
      }

      // Group member logic
      if (childIndex > -1) {
        building = this._toggleGroupMember(modelIndex, childIndex, building);
      }

      // Shared group and group member logic to toggle the group checkbox
      if (this._isGroupChecked(buildingIndex, modelIndex, building)) {
        this._toggleLonelyStat(modelIndex, building, "add");
      } else {
        this._toggleLonelyStat(modelIndex, building, "remove");
      }

      // Remove any empty groups
      if (
        building.groupStats[modelIndex] &&
        building.groupStats[modelIndex].length === 0
      ) {
        delete building.groupStats[modelIndex];
        building.groupStats = building.groupStats.filter(Boolean);
      }
    } else {
      // Non-group logic
      building = this._toggleLonelyStat(modelIndex, building, type);
    }

    buildings[buildingIndex] = building;
    this.setState(buildings);
  };

  _getSortedModel = (buildingIndex, modelIndex, childIndex = -1) => {
    const building = this.sortedBuildings[buildingIndex];
    return childIndex > -1
      ? building[modelIndex].getGroupMembers().models[childIndex]
      : building[modelIndex];
  };

  _getOrCreateBuilding = (buildingIndex) => {
    return this.state.buildings[buildingIndex]
      ? this.state.buildings[buildingIndex]
      : this._buildingTemplate();
  };

  _handleCancel = () => {
    if (!this.props.closeDialog) {
      return;
    }
    if (this.props.onCancel) {
      // Some components may have additional cancel requirements like state or dialog requirements
      this.props.onCancel();
    }
    this.props.closeDialog();
  };

  _handleApply = () => {
    if (!this.props.onChoose) {
      return;
    }
    const { buildings } = this.state;
    let ids = [];

    buildings.forEach((building, buildingIndex) => {
      const lonelyStats = building.lonelyStats;
      const groupStats = building.groupStats;
      lonelyStats.forEach((statIndex) => {
        let stat = this.sortedBuildings[buildingIndex][statIndex];
        if (stat.get("type") === "group") {
          return;
        }
        ids.push(stat.get("device_identifier"));
      });

      Object.entries(groupStats).forEach((group) => {
        const groupIndex = parseInt(group[0]);
        const selectedStats = group[1];
        const groupMembers = this.sortedBuildings[buildingIndex][
          groupIndex
        ].getGroupMembers().models;
        selectedStats.forEach((groupStatIndex) => {
          ids.push(groupMembers[groupStatIndex].get("device_identifier"));
        });
      });
    });

    this.props.onChoose(ids);
    this.props.closeDialog();
    Dispatch("FAST_POPULATE_SESSION");
  };

  _handleSelectItem = (buildingIndex, statIndex, childIndex) => {
    this._addRemoveThermostat(buildingIndex, statIndex, childIndex);
  };

  _toggleGroupMember(parentIndex, statIndex, building, type) {
    if (building.groupStats[parentIndex]) {
      const index = building.groupStats[parentIndex].indexOf(statIndex);
      if (type !== "add" && index >= 0) {
        building.groupStats[parentIndex].splice(index, 1);
      } else if (index === -1) {
        building.groupStats[parentIndex].push(statIndex);
      }
    } else {
      building.groupStats[parentIndex] = [statIndex];
    }
    return building;
  }

  _isGroupChecked(buildingIndex, groupIndex, building) {
    let isChecked = false;
    if (building.groupStats[groupIndex]) {
      const group = this._getSortedModel(buildingIndex, groupIndex);
      const membersCount = group.getGroupMembers().length;
      const membersChecked = building.groupStats[groupIndex].length;
      const groupId = this._getSortedModel(buildingIndex, groupIndex).get("id");
      let disabledCount = building.disabledGroupStats[groupId];
      disabledCount = disabledCount || 0;
      isChecked = disabledCount + membersChecked === membersCount;
    }
    return isChecked;
  }

  _toggleAllGroupStats(buildingIndex, groupIndex, members, type) {
    for (let statIndex = 0; statIndex < members.length; statIndex++) {
      if (this._isConnected(members[statIndex])) {
        this._addRemoveThermostat(buildingIndex, groupIndex, statIndex, type);
      }
    }
  }

  _toggleLonelyStat(statIndex, building, type = null) {
    const index = building.lonelyStats.indexOf(statIndex);
    let addRemove = type;
    if (!addRemove) {
      addRemove = index > -1 ? "remove" : "add";
    }
    if (addRemove === "remove" && index >= 0) {
      building.lonelyStats.splice(index, 1);
    } else if (addRemove !== "remove" && index < 0) {
      building.lonelyStats.push(statIndex);
    }
    return building;
  }

  _createCheckBox = (name, buildingIndex, index, childIndex = -1) => {
    const {
      lonelyStats,
      groupStats,
      disabledGroupStats,
    } = this.state.buildings[buildingIndex];
    let isChecked = false;
    let partialChecked = false;

    if (childIndex < 0) {
      if (lonelyStats.indexOf(index) >= 0) {
        isChecked = true;
      } else if (groupStats[index] && groupStats[index].length) {
        partialChecked = true;
      }
    } else if (
      groupStats[index] &&
      groupStats[index].indexOf(childIndex) >= 0
    ) {
      isChecked = true;
    }

    let isDisabled = false;
    const thermostat =
      childIndex >= 0
        ? this.sortedBuildings[buildingIndex][index].getGroupMembers().models[
            childIndex
          ]
        : this.sortedBuildings[buildingIndex][index];
    if (thermostat.get("type") === "group") {
      if (
        thermostat.getGroupMembers().length ===
        disabledGroupStats[thermostat.id]
      ) {
        isDisabled = true;
      }
    } else {
      isDisabled = !this._isConnected(thermostat);
    }

    let key = `${buildingIndex}${index}`;

    if (childIndex >= 0) {
      key = `${key}${childIndex}`;
    }

    const callback = () => {
      this._handleSelectItem(buildingIndex, index, childIndex);
    };

    return this._checkbox(
      key,
      name,
      isChecked,
      partialChecked,
      isDisabled,
      callback
    );
  };

  _createBuildingCheckbox = (buildingName, buildingIndex) => {
    const building = this.state.buildings[buildingIndex];
    const currentCount = building.lonelyStats.length;
    const isChecked =
      currentCount + building.totalDisabledStats ===
        this.deviceCounts[buildingIndex] &&
      this.deviceCounts[buildingIndex] > 0;
    const partialChecked =
      building.lonelyStats.length > 0 || building.groupStats.length > 0;
    const isDisabled =
      this.deviceCounts[buildingIndex] === building.totalDisabledStats;

    if (partialChecked || isChecked) {
      this.hasSelectedItems = true;
    }

    const callback = () => {
      this._handleSelectedBuilding(buildingIndex);
    };

    return this._checkbox(
      buildingIndex,
      buildingName,
      isChecked,
      partialChecked,
      isDisabled,
      callback
    );
  };

  _checkbox = (key, name, isChecked, partialChecked, isDisabled, callback) => {
    const styles = this._getStyles();
    return (
      <div
        style={{
          ...styles.checkbox,
          ...(isDisabled ? { color: "#dbdbdb" } : null),
        }}
        key={key}
      >
        <div style={{ display: "inline-block", position: "relative" }}>
          <Checkbox
            style={isDisabled ? { backgroundColor: "#cbcbcb" } : null}
            checked={isChecked}
            onTap={!isDisabled ? callback : null}
          />
          <PartialCheckbox
            style={styles.partialCheckbox}
            checked={partialChecked}
          />
        </div>
        <Tappable onTap={!isDisabled ? callback : null}>
          {({ tapTargetRefHandler }) => (
            <span style={styles.label} tabIndex={0} ref={tapTargetRefHandler}>
              {name}
            </span>
          )}
        </Tappable>
      </div>
    );
  };

  _handleSelectedBuilding = (buildingIndex) => {
    const { buildings } = this.state;
    const sortedBuilding = this.sortedBuildings[buildingIndex];
    let building = this.state.buildings[buildingIndex];
    const isAdding =
      building.lonelyStats.length + building.totalDisabledStats !==
      this.deviceCounts[buildingIndex];
    const type = isAdding ? "add" : "remove";
    sortedBuilding.forEach((device, index) => {
      if (device.get("type") !== "group") {
        this._addRemoveThermostat(buildingIndex, index, -1, type);
      } else {
        const isChecked = this._isGroupChecked(buildingIndex, index, building);
        if ((isAdding && !isChecked) || (!isAdding && isChecked)) {
          this._addRemoveThermostat(buildingIndex, index);
        }
      }
    });
    buildings[buildingIndex] = building;
    this.setState(buildings);
  };

  _constructList() {
    /* eslint-disable react/no-array-index-key */
    const styles = this._getStyles();
    const results = this.sortedBuildings.map((building, buildingIndex) => {
      const buildingHeader = this._createBuildingCheckbox(
        this.buildingNames[buildingIndex],
        buildingIndex
      );
      const checkboxes = building.map((model, itemIndex) => {
        const isGroup = model.get("type") === "group";
        let items;
        let header;

        if (isGroup) {
          header = this._createCheckBox(
            model.get("name"),
            buildingIndex,
            itemIndex
          );
          items = [];
          model
            .getGroupMembers()
            .map((device, groupIndex) =>
              items.push(
                this._createCheckBox(
                  device.get("name"),
                  buildingIndex,
                  itemIndex,
                  groupIndex
                )
              )
            );
        } else {
          items = this._createCheckBox(
            model.get("name"),
            buildingIndex,
            itemIndex
          );
        }

        return (
          <div key={itemIndex} style={styles.row}>
            <CollapsibleList open header={header} items={items} />
          </div>
        );
      });
      return (
        <div key={`checkbox_${buildingIndex}`} style={styles.row}>
          <CollapsibleList open header={buildingHeader} items={checkboxes} />
        </div>
      );
    });
    return results;
    /* eslint-enable react/no-array-index-key */
  }

  render() {
    const { open } = this.props;
    const styles = this._getStyles();

    this.hasSelectedItems = false;

    return (
      <Dialog
        open={open}
        onTapBackground={this._handleCancel}
        onRequestClose={this._handleCancel}
      >
        <div style={styles.container}>
          <div style={styles.title}>{this.language.thermostatsTitle}</div>
          <div style={styles.list}>{this._constructList()}</div>
          <div style={styles.buttons}>
            <Button
              onTap={this._handleCancel}
              style={styles.button}
              theme="ghost"
            >
              {this.language.cancel}
            </Button>
            <Button
              disabled={!this.hasSelectedItems}
              onTap={this._handleApply}
              style={styles.button}
            >
              {this.language.apply}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }
}

const PivotDevicesGroupDialogWithColors = withColorsProvider(
  PivotDevicesGroupsDialog
);

export function showPivotDevices(options) {
  renderInDestroyableRoot((destroyRoot) => {
    const dialogProps = { ...options };

    dialogProps.closeDialog = () => {
      destroyRoot();
      if (_.isFunction(options.closeDialog)) {
        options.closeDialog();
      }
    };

    return <PivotDevicesGroupDialogWithColors {...dialogProps} />;
  });
}



// WEBPACK FOOTER //
// ./src/components/Dialogs/Pivot/PivotDevicesGroupsDialog.jsx